import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DatetimePicker, Popup, Picker } from 'react-vant'
import Header from '@/components/Header'
import Back from '@/components/Back'
import FixedBottom from '@/components/FixedBottom'
import CustomButton from '@/components/CustomButton'
import CustomTextInput from '@/components/CustomTextInput'
import CountrySelector from '@/components/CountrySelector'
import CountryCodeSelector from '@/components/CountryCodeSelector'
import { useOccupationListStatus, OccupationType } from '@/hooks/listStatus'
import { type KycFormData, isCompleteInfoValid, isAtLeast18YearsOld } from '../types'
import dayjs from 'dayjs'

interface StepCompleteInfoProps {
  formData: KycFormData
  updateFormData: (data: Partial<KycFormData>) => void
  onNext: () => void
  onBack: () => void
}

const getGenderOptions = (t: (key: string) => string) => [
  { value: 'male', label: t('男') },
  { value: 'female', label: t('女') },
]

const occupationValues = Object.values(OccupationType)

const PHONE_REGEX = /^\d{6,15}$/

export default function StepCompleteInfo({ formData, updateFormData, onNext, onBack }: StepCompleteInfoProps) {
  const { t } = useTranslation()
  const { getOccupationListStatus } = useOccupationListStatus()
  const genderOptions = getGenderOptions(t)

  const canProceed = useMemo(() => isCompleteInfoValid(formData), [formData])

  const [showGenderPicker, setShowGenderPicker] = useState(false)
  const [showBirthdayPicker, setShowBirthdayPicker] = useState(false)
  const [showOccupationPicker, setShowOccupationPicker] = useState(false)

  const phoneError = useMemo(() => {
    if (!formData.phone) return ''
    return PHONE_REGEX.test(formData.phone) ? '' : t('请输入有效的手机号X位数字')
  }, [formData.phone, t])

  const birthDateError = useMemo(() => {
    if (!formData.birth_date?.trim()) return ''
    return isAtLeast18YearsOld(formData.birth_date) ? '' : t('需年满18周岁')
  }, [formData.birth_date, t])

  const handlePhoneChange = useCallback((value: string) => {
    const numericValue = value.replace(/[^\d]/g, '')
    updateFormData({ phone: numericValue })
  }, [updateFormData])

  const getGenderLabel = (value: string) => {
    const option = genderOptions.find(opt => opt.value === value)
    return option ? option.label : ''
  }

  const occupationOptions = occupationValues.map(value => ({
    text: getOccupationListStatus(value),
    value: value
  }))

  const handleNext = useCallback(() => {
    if (canProceed) {
      onNext()
    }
  }, [onNext, canProceed])

  const renderSelectInput = (
    label: string,
    value: string,
    placeholder: string,
    onClick: () => void,
    className?: string,
    isErrored?: boolean
  ) => (
    <div className={className || 'w-full'}>
      <div className="block text-sm text-[#667085] mb-2 font-medium">
        {label}
      </div>
      <div
        onClick={onClick}
        className={`w-full h-12 px-4 rounded-2.5 border border-solid ${isErrored ? 'border-red-500' : 'border-[#EAECF0]'} bg-white flex items-center justify-between cursor-pointer`}
      >
        <span className={value ? 'text-[#1D2129]' : 'text-[#C9CDD4]/70'}>
          {value || placeholder}
        </span>
        <img src="/icon/select-down.svg" alt="" className="w-3" />
      </div>
    </div>
  )

  return (
    <div className="flex flex-col h-full">
      <Header
        leftChildren={<Back onClick={onBack} />}
      />

      <div className="flex-1 px-5 pt-4 pb-24 overflow-y-auto">
        <div className="text-7 font-medium text-[#1D2129] mb-2">
          {t('信息完善')}
        </div>
        <p className="text-sm text-[#9BA0AF] mb-6">
          {t('所填信息须与官方签发证件信息一致')}
        </p>

        <div className="space-y-4">
          {/* 性别 */}
          {renderSelectInput(
            t('性别'),
            getGenderLabel(formData.sex),
            t('选择性别'),
            () => setShowGenderPicker(true)
          )}

          {/* 手机号 */}
          <div>
            <div className="block text-sm text-[#667085] mb-2 font-medium">
              {t('手机号')}
            </div>
            <div className={`flex items-center h-12 rounded-2.5 border border-solid bg-white ${phoneError ? 'border-red-500' : 'border-[#EAECF0]'}`}>
              <div className="h-full flex items-center pl-4 pr-2 [&>div]:border-none [&>div]:h-auto [&>div]:p-0 [&>div]:rounded-none cursor-pointer">
                <CountryCodeSelector
                  value={formData.phone_zone_number}
                  onChange={(dialCode) => updateFormData({ phone_zone_number: dialCode })}
                />
              </div>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder={t('输入手机号')}
                inputMode="numeric"
                className="flex-1 h-full px-3 outline-none text-[#1D2129] placeholder-[#C9CDD4] bg-transparent"
              />
            </div>
            {phoneError && (
              <div className="text-xs text-red-500 mt-1">{phoneError}</div>
            )}
          </div>

          {/* 证件颁发国家或地区 */}
          <div>
            <div className="block text-sm text-[#667085] mb-2 font-medium">
              {t('证件颁发国家或地区')}
            </div>
            <CountrySelector
              value={formData.nationality}
              onChange={(value) => updateFormData({ nationality: value })}
              placeholder={t('请选择')}
              returnType="value"
            />
          </div>

          {/* 出生日期 */}
          <div className="w-full">
            {renderSelectInput(
              t('出生日期'),
              formData.birth_date ? dayjs(formData.birth_date).format('YYYY/MM/DD') : '',
              'YYYY/MM/DD',
              () => setShowBirthdayPicker(true),
              undefined,
              !!birthDateError
            )}
            {birthDateError && (
              <div className="text-xs text-red-500 mt-1">{birthDateError}</div>
            )}
          </div>

          {/* 职务 */}
          {renderSelectInput(
            t('职务'),
            formData.occupation ? getOccupationListStatus(formData.occupation) : '',
            t('选择职务'),
            () => setShowOccupationPicker(true)
          )}

          {/* 省份 & 城市 */}
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="block text-sm text-[#667085] mb-2 font-medium">
                {t('省份')}
              </div>
              <CustomTextInput
                value={formData.address_province}
                onChangeText={(val) => updateFormData({ address_province: val })}
                placeholder={t('所在省份')}
              />
            </div>
            <div className="flex-1">
              <div className="block text-sm text-[#667085] mb-2 font-medium">
                {t('城市')}
              </div>
              <CustomTextInput
                value={formData.address_city}
                onChangeText={(val) => updateFormData({ address_city: val })}
                placeholder={t('所在城市')}
              />
            </div>
          </div>

          {/* 详细地址 */}
          <div>
            <div className="block text-sm text-[#667085] mb-2 font-medium">
              {t('详细地址')}
            </div>
            <textarea
              value={formData.address}
              onChange={(e) => updateFormData({ address: e.target.value })}
              placeholder={t('填写详细地址')}
              rows={3}
              className="w-full px-4 py-3 rounded-2.5 border border-solid border-[#EAECF0] bg-white text-[#1D2129] placeholder-[#C9CDD4] resize-none focus:border-[#667085]"
            />
          </div>
        </div>
      </div>

      <FixedBottom className="bg-white">
        <CustomButton onClick={handleNext} disabled={!canProceed}>
          {t('下一步')}
        </CustomButton>
      </FixedBottom>

      {/* 性别选择器 */}
      <Popup
        visible={showGenderPicker}
        position="bottom"
        round
        onClose={() => setShowGenderPicker(false)}
      >
        <Picker
          title={t('选择性别')}
          placeholder={t('请选择')}
          cancelButtonText={t('取消')}
          confirmButtonText={t('确定')}
          columns={genderOptions.map(opt => ({ text: opt.label, value: opt.value }))}
          onConfirm={(value: string) => {
            updateFormData({ sex: value })
            setShowGenderPicker(false)
          }}
          onCancel={() => setShowGenderPicker(false)}
        />
      </Popup>

      {/* 出生日期选择器 */}
      <Popup
        visible={showBirthdayPicker}
        position="bottom"
        round
        onClose={() => setShowBirthdayPicker(false)}
      >
        <DatetimePicker
          type="date"
          title={t('选择出生日期')}
          cancelButtonText={t('取消')}
          confirmButtonText={t('确定')}
          minDate={new Date(1900, 0, 1)}
          maxDate={new Date()}
          value={formData.birth_date ? dayjs(formData.birth_date).toDate() : new Date(2000, 0, 1)}
          onConfirm={(value: Date) => {
            updateFormData({ birth_date: dayjs(value).format('YYYY-MM-DD') })
            setShowBirthdayPicker(false)
          }}
          onCancel={() => setShowBirthdayPicker(false)}
        />
      </Popup>

      {/* 职务选择器 */}
      <Popup
        visible={showOccupationPicker}
        position="bottom"
        round
        onClose={() => setShowOccupationPicker(false)}
      >
        <Picker
          title={t('选择职务')}
          cancelButtonText={t('取消')}
          confirmButtonText={t('确定')}
          placeholder={t('请选择')}
          columns={occupationOptions}
          onConfirm={(value: string) => {
            updateFormData({ occupation: value })
            setShowOccupationPicker(false)
          }}
          onCancel={() => setShowOccupationPicker(false)}
        />
      </Popup>
    </div>
  )
}
